/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from "react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import {
  FileDown, Sparkles, Award, GraduationCap, TrendingUp, Calendar,
  Activity, AlertTriangle, CheckCircle2, ShieldAlert, Lightbulb,
  Briefcase, Target, Rocket, Quote, Loader2,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const fmtBRL = (n) => (n == null ? "—" : Number(n).toLocaleString("pt-BR", { style: "currency", currency: "BRL" }));
const fmtDate = (iso) => {
  if (!iso) return "—";
  const s = String(iso);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("pt-BR");
};
const fmtMonths = (m) => {
  if (m == null) return "—";
  if (m < 12) return `${m}m`;
  const y = Math.floor(m / 12); const r = m % 12;
  return r === 0 ? `${y}a` : `${y}a${r}m`;
};

const SITUATION_CFG = {
  favoravel: { label: "Situação Favorável", color: "#1F8136", bg: "#E5F4E8", icon: CheckCircle2 },
  atencao:   { label: "Atenção",            color: "#B26A3D", bg: "#FFF3E0", icon: AlertTriangle },
  critica:   { label: "Crítica",            color: "#B91C1C", bg: "#FEE2E2", icon: ShieldAlert },
};

export default function EmployeeDossierPDF({ employeeId, open, onClose }) {
  const docRef = useRef(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    if (!open || !employeeId) return;
    setLoading(true);
    setData(null);
    api.get(`/performance/employee/${employeeId}/dossier`, { params: { include_ai: true } })
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || "Falha ao gerar dossiê"))
      .finally(() => setLoading(false));
  }, [open, employeeId]);

  const downloadPdf = async () => {
    if (!docRef.current || !data) return;
    setBusy("pdf");
    try {
      const canvas = await html2canvas(docRef.current, {
        scale: 2, useCORS: true, backgroundColor: "#ffffff",
      });
      const img = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const ratio = canvas.height / canvas.width;
      const imgW = pageW - 14;
      const imgH = imgW * ratio;
      if (imgH <= pageH - 14) {
        pdf.addImage(img, "PNG", 7, 7, imgW, imgH);
      } else {
        const pageImgH = pageH - 14;
        const sliceH = (canvas.width / imgW) * pageImgH;
        let sY = 0;
        while (sY < canvas.height) {
          const c2 = document.createElement("canvas");
          c2.width = canvas.width;
          c2.height = Math.min(sliceH, canvas.height - sY);
          const ctx = c2.getContext("2d");
          ctx.drawImage(canvas, 0, sY, canvas.width, c2.height, 0, 0, canvas.width, c2.height);
          pdf.addImage(c2.toDataURL("image/png"), "PNG", 7, 7, imgW, (c2.height / canvas.width) * imgW);
          sY += c2.height;
          if (sY < canvas.height) pdf.addPage();
        }
      }
      const safeName = data.individual.employee.nome.replace(/[\s/\\]/g, "_");
      pdf.save(`Dossie-${safeName}.pdf`);
      toast.success("Dossiê PDF baixado.");
    } catch (e) {
      toast.error("Falha ao gerar PDF.");
    } finally { setBusy(null); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose && onClose()}>
      <DialogContent className="sm:max-w-[920px] max-h-[94vh] overflow-y-auto p-0 gap-0"
                     aria-describedby="dossier-desc" data-testid="dossier-dialog">
        <DialogHeader className="px-6 pt-5 pb-3 border-b border-border bg-white sticky top-0 z-10">
          <div className="flex items-center justify-between gap-2">
            <DialogTitle className="text-[17px] flex items-center gap-2">
              <Briefcase className="h-4 w-4 text-[#234935]" />
              Dossiê Individual do Colaborador
            </DialogTitle>
            <Button size="sm" onClick={downloadPdf} disabled={busy !== null || loading || !data}
                    className="rounded-full bg-[#234935] hover:bg-[#2c5c43] text-white px-5"
                    data-testid="dossier-download-btn">
              {busy === "pdf"
                ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                : <FileDown className="h-3.5 w-3.5 mr-1.5" />}
              Baixar PDF
            </Button>
          </div>
        </DialogHeader>
        <p id="dossier-desc" className="sr-only">Dossiê executivo individual do colaborador</p>

        <div className="bg-slate-100 p-3 sm:p-5">
          {loading || !data ? (
            <Skeleton className="h-[1100px]" />
          ) : (
            <div ref={docRef}>
              <DossierLayout data={data} />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DossierLayout({ data }) {
  const { individual, recommended_trainings, onboarding, ai, manager,
          final_recommendation, situation } = data;
  const emp = individual.employee;
  const company = individual.company || {};
  const sector = individual.sector || {};
  const cons = individual.consolidated;
  const sit = SITUATION_CFG[situation] || SITUATION_CFG.atencao;
  const SitIcon = sit.icon;
  const aiOk = ai && ai.analysis && !ai.error;

  return (
    <div className="mx-auto bg-white shadow-xl"
         style={{ width: "100%", maxWidth: 820, fontFamily: "system-ui, -apple-system, 'Segoe UI', sans-serif" }}
         data-testid="dossier-document">
      {/* HEADER */}
      <div className="px-7 py-5 text-white"
           style={{ background: "linear-gradient(135deg, #234935 0%, #1A231F 100%)" }}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            {company.logo_url && (
              <div className="h-12 w-12 rounded bg-white p-1.5 shadow">
                <img src={company.logo_url} alt="logo" className="h-full w-full object-contain" crossOrigin="anonymous" />
              </div>
            )}
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#C5A059] font-semibold">TheoKratos RH</div>
              <div className="font-display text-[17px] font-semibold leading-tight">Dossiê Individual do Colaborador</div>
              <div className="text-[10.5px] text-white/70 mt-0.5">Gerado em {fmtDate(new Date().toISOString())} · {company.nome_fantasia || "—"}</div>
            </div>
          </div>
          <span className="rounded-full px-3 py-1.5 text-[10.5px] font-bold uppercase tracking-wider"
                style={{ background: sit.bg, color: sit.color }}
                data-testid="dossier-situation">
            {sit.label}
          </span>
        </div>

        <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1.5 text-[10.5px]">
          <Field label="Colaborador" value={emp.nome} bold />
          <Field label="Matrícula" value={emp.matricula} />
          <Field label="Status" value={emp.status} cap />
          <Field label="Empresa" value={company.nome_fantasia} />
          <Field label="Setor" value={sector.name} />
          <Field label="Cargo" value={emp.cargo} />
          <Field label="Turno" value={emp.turno} />
          <Field label="Admissão" value={fmtDate(emp.data_admissao)} />
          <Field label="Tempo de empresa" value={fmtMonths(cons.tempo_empresa_meses)} />
          <Field label="Salário" value={fmtBRL(emp.salario)} />
          <Field label="Gestor" value={manager?.name || "—"} />
        </div>
      </div>

      {/* RESUMO EXECUTIVO STRIP */}
      <div className="grid grid-cols-2 sm:grid-cols-6 gap-2 px-7 py-4 border-b border-slate-200">
        <Stat icon={Award} label="Score geral" value={cons.media_avaliacoes ?? "—"}
              hint={cons.media_setor != null ? `Setor: ${cons.media_setor}` : "—"} />
        <Stat icon={GraduationCap} label="Treinos" value={individual.training_kpis.concluidos}
              hint={`${individual.training_kpis.taxa_conclusao_pct}%`} />
        <Stat icon={TrendingUp} label="Promoções" value={cons.total_promocoes}
              hint={cons.ultima_promocao_em ? fmtDate(cons.ultima_promocao_em) : "Sem"} />
        <Stat icon={Calendar} label="Tempo cargo" value={fmtMonths(cons.tempo_cargo_meses)} />
        <Stat icon={Rocket} label="Onboarding"
              value={onboarding ? `${onboarding.progress_pct}%` : "—"}
              hint={onboarding ? `D${onboarding.days_since_start}/90` : "Sem trilha"} />
        <Stat icon={Activity} label="Absenteísmo"
              value={individual.absences.data_status === "ok" ? `${individual.absences.horas_perdidas_12m}h` : "—"}
              hint={individual.absences.data_status === "ok"
                    ? `${individual.absences.ocorrencias_12m} ocorrências` : "Sem dados"} />
      </div>

      {/* MAIN GRID */}
      <div className="grid grid-cols-2 gap-3 p-5">
        <Card title="Avaliação de desempenho" icon={Award}>
          {individual.appraisals.length === 0 ? (
            <Empty>Sem avaliações registradas.</Empty>
          ) : (
            <>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[10.5px] text-slate-600">{individual.appraisals[0].cycle_name}</span>
                <span className="font-display text-[20px] font-bold tabular-nums text-[#234935]">
                  {individual.appraisals[0].final_score}
                  <span className="text-[10px] text-slate-400">/5</span>
                </span>
              </div>
              {cons.diferenca_setor != null && (
                <div className={`text-[10.5px] mb-2 ${cons.diferenca_setor >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                  {cons.diferenca_setor >= 0 ? "▲" : "▼"} {Math.abs(cons.diferenca_setor)} vs setor (média {cons.media_setor})
                </div>
              )}
              {individual.competency_gaps.length > 0 && (
                <>
                  <div className="text-[9.5px] uppercase tracking-wider text-[#8A6B2B] font-semibold mt-2 mb-1">Pontos de melhoria</div>
                  <div className="space-y-1">
                    {individual.competency_gaps.slice(0, 4).map((g) => (
                      <div key={g.competency} className="flex items-center gap-2">
                        <span className="text-[10.5px] capitalize flex-1">{g.competency.replace(/_/g, " ")}</span>
                        <div className="flex-1 max-w-[80px] h-1 rounded-full bg-slate-100 overflow-hidden">
                          <div className={`h-full ${g.score < 3 ? "bg-rose-500" : g.score < 4 ? "bg-[#C5A059]" : "bg-emerald-500"}`}
                               style={{ width: `${(g.score / 5) * 100}%` }} />
                        </div>
                        <span className="text-[10px] font-mono w-7 text-right">{g.score}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </Card>

        <Card title="PDI / Desenvolvimento" icon={Target}>
          {individual.appraisals.length === 0 ? (
            <Empty>Sem PDI vinculado.</Empty>
          ) : (
            <div className="text-[11px] text-slate-700 space-y-1">
              <div>Ciclos avaliados: <b>{cons.total_avaliacoes}</b></div>
              <div>Gaps identificados: <b>{individual.competency_gaps.length}</b></div>
              <div>Última avaliação concluída em: <b>{fmtDate(individual.appraisals[0].completed_at)}</b></div>
              {individual.appraisals[0].manager_comment && (
                <div className="mt-1.5 italic text-[10.5px] text-slate-600 line-clamp-3">
                  "{individual.appraisals[0].manager_comment}"
                </div>
              )}
            </div>
          )}
        </Card>

        <Card title="Treinamentos recomendados" icon={GraduationCap}>
          {recommended_trainings.items.length === 0 ? (
            <Empty>Nenhum treinamento ativo elegível no momento.</Empty>
          ) : (
            <ul className="space-y-1 text-[10.5px]">
              {recommended_trainings.items.slice(0, 5).map((t) => (
                <li key={t.training_id} className="flex items-start gap-1.5">
                  <Target className="h-3 w-3 text-[#C5A059] mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{t.name}</div>
                    {t.reasons[0] && <div className="text-[9.5px] text-slate-500 line-clamp-1">{t.reasons[0]}</div>}
                  </div>
                  <span className="text-[9px] font-bold text-[#234935]">{t.score}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card title="Onboarding" icon={Rocket}>
          {!onboarding ? (
            <Empty>Sem trilha de onboarding aplicada.</Empty>
          ) : (
            <>
              <div className="text-[10.5px] mb-1.5">
                <b>{onboarding.template_name}</b> · Dia {onboarding.days_since_start}/90 · Status: <b className="capitalize">{(onboarding.status || "").replace("_", " ")}</b>
              </div>
              <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden mb-2">
                <div className="h-full bg-[#234935]" style={{ width: `${onboarding.progress_pct}%` }} />
              </div>
              {onboarding.by_periodo && (
                <div className="grid grid-cols-3 gap-1 text-center">
                  {onboarding.by_periodo.map((p) => (
                    <div key={p.periodo} className="rounded-md bg-slate-50 p-1">
                      <div className="text-[8.5px] uppercase tracking-wider text-slate-500">D{p.periodo}</div>
                      <div className="text-[10.5px] font-mono tabular-nums">{p.concluidas}/{p.total}</div>
                      {p.atrasadas > 0 && <div className="text-[8.5px] text-rose-700">{p.atrasadas} atras.</div>}
                    </div>
                  ))}
                </div>
              )}
              {onboarding.recomendacao_final && (
                <div className="mt-2 text-[10.5px]">
                  Recomendação 90d: <b className="capitalize text-[#234935]">{onboarding.recomendacao_final}</b>
                </div>
              )}
            </>
          )}
        </Card>

        <Card title="Absenteísmo (12 meses)" icon={Activity}>
          {individual.absences.data_status === "vazio" ? (
            <Empty>Sem dados de absenteísmo no sistema.</Empty>
          ) : (
            <div className="grid grid-cols-2 gap-2 text-center">
              <div className="rounded-md bg-slate-50 p-1.5">
                <div className="text-[8.5px] uppercase tracking-wider text-slate-500">Horas perdidas</div>
                <div className="text-[16px] font-display font-semibold tabular-nums">{individual.absences.horas_perdidas_12m}h</div>
              </div>
              <div className="rounded-md bg-slate-50 p-1.5">
                <div className="text-[8.5px] uppercase tracking-wider text-slate-500">Ocorrências</div>
                <div className="text-[16px] font-display font-semibold tabular-nums">{individual.absences.ocorrencias_12m}</div>
              </div>
            </div>
          )}
        </Card>

        <Card title="Histórico de promoções" icon={TrendingUp}>
          {individual.promotions.length === 0 ? (
            <Empty>Sem promoções registradas.</Empty>
          ) : (
            <ul className="space-y-1 text-[10.5px]">
              {individual.promotions.slice(0, 3).map((p) => (
                <li key={p.id} className="flex items-center justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-[9px] text-slate-400">{fmtDate(p.issued_at)}</div>
                    <div className="truncate">
                      <span className="text-slate-500">{p.cargo_anterior}</span> → <b>{p.new_cargo}</b>
                    </div>
                  </div>
                  {p.diff_pct != null && <span className="text-[10px] text-emerald-700 font-bold">+{p.diff_pct}%</span>}
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      {/* IA SECTION */}
      <div className="px-5 pb-5">
        <div className="rounded-lg overflow-hidden border-2 border-[#234935]/20" data-testid="dossier-ai">
          <div className="px-4 py-2.5 flex items-center gap-2 text-white"
               style={{ background: "linear-gradient(135deg, #234935 0%, #1A231F 100%)" }}>
            <Sparkles className="h-4 w-4 text-[#C5A059]" />
            <span className="text-[10.5px] uppercase tracking-[0.22em] text-[#C5A059] font-semibold">Análise IA · Claude Sonnet 4.5</span>
          </div>
          <div className="px-4 py-3 bg-white">
            {!aiOk ? (
              <div className="text-[11px] text-slate-500 italic">
                {ai?.error
                  ? `Análise IA indisponível: ${ai.error}`
                  : "Análise IA pendente — gere a análise primeiro na aba IA do colaborador."}
              </div>
            ) : (
              <>
                <p className="text-[12px] text-slate-800 leading-relaxed mb-2.5 italic">
                  <Quote className="inline h-3 w-3 text-[#C5A059] mr-0.5 -mt-0.5" />
                  {ai.analysis.resumo_executivo}
                </p>
                <div className="grid grid-cols-2 gap-3 text-[10.5px]">
                  <div>
                    <div className="text-[9.5px] uppercase tracking-wider text-emerald-700 font-semibold mb-1">Pontos fortes</div>
                    <ul className="space-y-0.5">
                      {(ai.analysis.pontos_fortes || []).slice(0, 3).map((p, i) => (
                        <li key={`pf-${p.slice(0, 12)}-${i}`} className="flex items-start gap-1">
                          <span className="text-emerald-700 mt-0.5">›</span><span>{p}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <div className="text-[9.5px] uppercase tracking-wider text-amber-700 font-semibold mb-1">Atenção</div>
                    <ul className="space-y-0.5">
                      {(ai.analysis.pontos_atencao || []).slice(0, 3).map((p, i) => (
                        <li key={`pa-${p.slice(0, 12)}-${i}`} className="flex items-start gap-1">
                          <span className="text-amber-700 mt-0.5">›</span><span>{p}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                <div className="mt-3 pt-2 border-t border-slate-100">
                  <div className="text-[9.5px] uppercase tracking-wider text-[#8A6B2B] font-semibold mb-1 flex items-center gap-1">
                    <Lightbulb className="h-3 w-3" /> Recomendações ao gestor
                  </div>
                  <ol className="space-y-0.5 text-[10.5px]">
                    {(ai.analysis.recomendacoes || []).slice(0, 4).map((r, i) => (
                      <li key={`rec-${r.slice(0, 12)}-${i}`}>
                        <b className="text-[#C5A059]">{i + 1}.</b> {r}
                      </li>
                    ))}
                  </ol>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* RECOMENDAÇÃO FINAL */}
      <div className="px-5 pb-6">
        <div className="rounded-lg p-4 flex items-center gap-3"
             style={{ background: sit.bg, border: `2px solid ${sit.color}` }}
             data-testid="dossier-final-rec">
          <SitIcon className="h-7 w-7 shrink-0" style={{ color: sit.color }} />
          <div className="flex-1">
            <div className="text-[9.5px] uppercase tracking-[0.22em] font-semibold" style={{ color: sit.color }}>
              Recomendação Gerencial
            </div>
            <div className="font-display text-[17px] font-bold" style={{ color: sit.color }}>
              {final_recommendation}
            </div>
            {aiOk && ai.analysis.justificativa_risco && (
              <div className="text-[10.5px] text-slate-700 mt-0.5 italic">
                {ai.analysis.justificativa_risco}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="px-5 py-3 bg-[#1A231F] text-white text-[9.5px] text-center">
        <span className="opacity-70">Documento gerado por TheoKratos RH · Confidencial · Uso interno</span>
      </div>
    </div>
  );
}

function Field({ label, value, bold, cap }) {
  return (
    <div>
      <div className="text-white/60 text-[8.5px] uppercase tracking-wider">{label}</div>
      <div className={`${bold ? "font-semibold" : ""} ${cap ? "capitalize" : ""}`}>{value || "—"}</div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, hint }) {
  return (
    <div className="text-center">
      <Icon className="h-3.5 w-3.5 mx-auto text-[#C5A059] mb-0.5" />
      <div className="text-[8.5px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-[14px] font-display font-bold tabular-nums leading-tight">{value}</div>
      {hint && <div className="text-[8.5px] text-slate-400">{hint}</div>}
    </div>
  );
}

function Card({ title, icon: Icon, children }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3" style={{ pageBreakInside: "avoid" }}>
      <div className="flex items-center gap-1.5 mb-2 pb-1.5 border-b border-slate-100">
        <Icon className="h-3.5 w-3.5 text-[#234935]" />
        <span className="font-display text-[11.5px] font-semibold text-[#234935]">{title}</span>
      </div>
      {children}
    </div>
  );
}

function Empty({ children }) {
  return <div className="text-[10.5px] text-slate-400 italic">{children}</div>;
}
